import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  SelectField,
  TextField,
  useConfirm,
  useToast,
  type Tone,
} from '@cartly/ui';
import { Megaphone } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../api/client';
import { formatRelative } from '../../app/format';
import { useLoader, useSubmit } from '../../app/hooks';
import type { SendablePhone, SendableTemplate } from './Messaging';

export type ParamSource = { source: string; value?: string };

const CUSTOMER_SOURCES = [
  { value: 'customer.firstName', label: 'Customer first name' },
  { value: 'customer.fullName', label: 'Customer full name' },
  { value: 'store.name', label: 'Store name' },
  { value: 'literal', label: 'Fixed text' },
];
const ORDER_SOURCES = [
  { value: 'order.number', label: 'Order number' },
  { value: 'order.total', label: 'Order total' },
  { value: 'order.currency', label: 'Order currency' },
  { value: 'order.paymentLink', label: 'Payment link (COD to prepaid)' },
  { value: 'order.carrier', label: 'Courier name' },
  { value: 'order.trackingNumber', label: 'Tracking number' },
  { value: 'order.trackingUrl', label: 'Tracking link' },
];
const PRODUCT_SOURCES = [
  { value: 'product.title', label: 'Product name' },
  { value: 'product.url', label: 'Product link' },
  { value: 'product.price', label: 'Current price' },
  { value: 'product.oldPrice', label: 'Price when they asked' },
];
export const ALERT_KINDS = ['back_in_stock', 'price_drop'];

/** One selector per `{{n}}` placeholder of the chosen template body. */
export function ParamsEditor({
  count,
  value,
  onChange,
  withOrder,
  withProduct = false,
}: {
  count: number;
  value: ParamSource[];
  onChange: (v: ParamSource[]) => void;
  withOrder: boolean;
  withProduct?: boolean;
}) {
  const options = withProduct
    ? [...CUSTOMER_SOURCES, ...PRODUCT_SOURCES]
    : withOrder
      ? [...CUSTOMER_SOURCES, ...ORDER_SOURCES]
      : CUSTOMER_SOURCES;
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const p = value[i] ?? { source: 'customer.firstName' };
        const set = (patch: ParamSource) => {
          const next = Array.from(
            { length: count },
            (_, j) => value[j] ?? { source: 'customer.firstName' },
          );
          next[i] = patch;
          onChange(next);
        };
        return (
          <div key={i} className="row">
            <SelectField
              label={`Parameter {{${i + 1}}}`}
              value={p.source}
              onChange={(e) =>
                set(
                  e.target.value === 'literal'
                    ? { source: 'literal', value: '' }
                    : { source: e.target.value },
                )
              }
              options={options}
            />
            {p.source === 'literal' && (
              <TextField
                label="Text"
                value={p.value ?? ''}
                onChange={(e) => set({ source: 'literal', value: e.target.value })}
                maxLength={200}
                required
              />
            )}
          </div>
        );
      })}
    </>
  );
}

export const normalizeParams = (count: number, v: ParamSource[]) =>
  Array.from({ length: count }, (_, i) => v[i] ?? { source: 'customer.firstName' });

const KIND_LABEL: Record<string, { title: string; when: string }> = {
  order_confirmation: { title: 'Order confirmation', when: 'When an order is placed' },
  payment_confirmation: { title: 'Payment confirmation', when: 'When an order is paid' },
  shipping_update: { title: 'Shipping update', when: 'When an order is fulfilled' },
  cod_verification: { title: 'COD verification', when: 'When a cash-on-delivery order is placed' },
  back_in_stock: { title: 'Back in stock', when: 'When a sold-out product is available again' },
  price_drop: { title: 'Price drop', when: 'When a product gets cheaper than when they asked' },
};

interface Rule {
  kind: string;
  enabled: boolean;
  phoneNumberId: string;
  templateName: string;
  templateLanguage: string;
  params: ParamSource[];
}

function RuleForm({
  kind,
  rule,
  phones,
  templates,
  onSaved,
}: {
  kind: string;
  rule: Rule | undefined;
  phones: SendablePhone[];
  templates: SendableTemplate[];
  onSaved: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [phoneId, setPhoneId] = useState(rule?.phoneNumberId ?? phones[0]?.id ?? '');
  const [tpl, setTpl] = useState(
    rule
      ? `${rule.templateName}|${rule.templateLanguage}`
      : `${approved[0]?.name}|${approved[0]?.language}`,
  );
  const [params, setParams] = useState<ParamSource[]>(rule?.params ?? []);
  const selected = approved.find((t) => `${t.name}|${t.language}` === tpl) ?? approved[0];
  const count = selected?.bodyParams ?? 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const ok = await submit.run(async () => {
      await api.put(`/whatsapp/transactional/${kind}`, {
        enabled,
        phoneNumberId: phoneId,
        templateName: selected.name,
        templateLanguage: selected.language,
        params: normalizeParams(count, params),
      });
      return true;
    });
    if (ok) {
      toast.success('Saved');
      onSaved();
    }
  };

  return (
    <form className="rule-card stack" onSubmit={(e) => void onSubmit(e)}>
      <div className="rule-card__head">
        <div>
          <div className="rule-card__title">{KIND_LABEL[kind]?.title ?? kind}</div>
          <div className="text-muted text-sm">{KIND_LABEL[kind]?.when ?? ''}</div>
        </div>
        <Badge tone={rule ? (rule.enabled ? 'success' : 'neutral') : 'warning'}>
          {rule ? (rule.enabled ? 'on' : 'off') : 'not set up'}
        </Badge>
      </div>
      {submit.error && <Alert tone="critical">{submit.error}</Alert>}
      <SelectField
        label="Status"
        value={enabled ? 'on' : 'off'}
        onChange={(e) => setEnabled(e.target.value === 'on')}
        options={[
          { value: 'on', label: 'Send automatically' },
          { value: 'off', label: 'Off' },
        ]}
      />
      <SelectField
        label="Send from"
        value={phoneId}
        onChange={(e) => setPhoneId(e.target.value)}
        options={phones.map((p) => ({ value: p.id, label: p.label }))}
      />
      <SelectField
        label="Template"
        value={selected ? `${selected.name}|${selected.language}` : ''}
        onChange={(e) => {
          setTpl(e.target.value);
          setParams([]);
        }}
        options={approved.map((t) => ({
          value: `${t.name}|${t.language}`,
          label: `${t.name} (${t.language})`,
        }))}
      />
      <ParamsEditor
        count={count}
        value={params}
        onChange={setParams}
        withOrder={!ALERT_KINDS.includes(kind)}
        withProduct={ALERT_KINDS.includes(kind)}
      />
      <div>
        <Button
          type="submit"
          variant="secondary"
          loading={submit.pending}
          disabled={!selected || !phoneId}
        >
          Save
        </Button>
      </div>
    </form>
  );
}

const CAMPAIGN_TONE: Record<string, Tone> = {
  scheduled: 'warning',
  running: 'info',
  completed: 'success',
  cancelled: 'neutral',
  failed: 'critical',
};

interface CampaignRow {
  id: string;
  name: string;
  templateName: string;
  segmentName: string;
  status: string;
  scheduledAt: string;
  totalRecipients: number | null;
  failureReason: string | null;
}
interface SegmentOption {
  id: string;
  name: string;
  customers: number;
}

function CampaignForm({
  phones,
  templates,
  segments,
  onCreated,
}: {
  phones: SendablePhone[];
  templates: SendableTemplate[];
  segments: SegmentOption[];
  onCreated: () => void;
}) {
  const toast = useToast();
  const submit = useSubmit();
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [name, setName] = useState('');
  const [phoneId, setPhoneId] = useState(phones[0]?.id ?? '');
  const [tpl, setTpl] = useState('');
  const [segmentId, setSegmentId] = useState(segments[0]?.id ?? '');
  const [params, setParams] = useState<ParamSource[]>([]);
  const [when, setWhen] = useState('');
  const selected = approved.find((t) => `${t.name}|${t.language}` === tpl) ?? approved[0];
  const count = selected?.bodyParams ?? 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    const ok = await submit.run(async () => {
      await api.post('/whatsapp/campaigns', {
        name: name.trim(),
        phoneNumberId: phoneId,
        templateName: selected.name,
        templateLanguage: selected.language,
        params: normalizeParams(count, params),
        segmentId: segmentId || segments[0]?.id,
        ...(when ? { scheduledAt: new Date(when).toISOString() } : {}),
      });
      return true;
    });
    if (ok) {
      toast.success(when ? 'Campaign scheduled' : 'Campaign started');
      setName('');
      onCreated();
    }
  };

  return (
    <form className="stack" onSubmit={(e) => void onSubmit(e)}>
      {submit.error && <Alert tone="critical">{submit.error}</Alert>}
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={120}
        required
      />
      <SelectField
        label="Audience (segment)"
        value={segmentId || segments[0]?.id || ''}
        onChange={(e) => setSegmentId(e.target.value)}
        options={segments.map((s) => ({ value: s.id, label: `${s.name} (${s.customers})` }))}
      />
      <SelectField
        label="Send from"
        value={phoneId}
        onChange={(e) => setPhoneId(e.target.value)}
        options={phones.map((p) => ({ value: p.id, label: p.label }))}
      />
      <SelectField
        label="Template"
        value={selected ? `${selected.name}|${selected.language}` : ''}
        onChange={(e) => {
          setTpl(e.target.value);
          setParams([]);
        }}
        options={approved.map((t) => ({
          value: `${t.name}|${t.language}`,
          label: `${t.name} (${t.language})`,
        }))}
      />
      <ParamsEditor count={count} value={params} onChange={setParams} withOrder={false} />
      <TextField
        label="Send at"
        type="datetime-local"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        hint="Leave empty to start right away. Only customers with a recorded opt-in receive the message."
      />
      <div>
        <Button
          type="submit"
          variant="primary"
          loading={submit.pending}
          disabled={!selected || segments.length === 0 || !name.trim()}
        >
          {when ? 'Schedule campaign' : 'Start campaign'}
        </Button>
      </div>
    </form>
  );
}

/** Order-event messages and campaigns on the WhatsApp page. */
export function CampaignSections({
  phones,
  templates,
  tenantId,
  canManageChannels,
  canReadCampaigns,
  canManageCampaigns,
  show,
}: {
  show: 'campaigns' | 'orderMessages' | 'alertMessages';
  phones: SendablePhone[];
  templates: SendableTemplate[];
  tenantId: string | undefined;
  canManageChannels: boolean;
  canReadCampaigns: boolean;
  canManageCampaigns: boolean;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const rules = useLoader(
    () =>
      api
        .get<{ kinds: string[]; rules: Rule[] }>('/whatsapp/transactional')
        .catch(() => ({ kinds: [] as string[], rules: [] as Rule[] })),
    tenantId,
  );
  const campaigns = useLoader(
    () =>
      canReadCampaigns
        ? api.get<{ campaigns: CampaignRow[] }>('/whatsapp/campaigns').then((r) => r.campaigns)
        : Promise.resolve([] as CampaignRow[]),
    tenantId,
  );
  const segments = useLoader(
    () =>
      canManageCampaigns
        ? api.get<{ segments: SegmentOption[] }>('/crm/segments').then((r) => r.segments)
        : Promise.resolve([] as SegmentOption[]),
    tenantId,
  );

  if (phones.length === 0) return null;
  const hasApproved = templates.some((t) => t.status === 'APPROVED');

  const cancel = async (c: CampaignRow) => {
    const ok = await confirm({
      title: 'Cancel campaign',
      message: `Cancel “${c.name}”? Messages that were not sent yet will not be sent.`,
      confirmLabel: 'Cancel campaign',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await api.post(`/whatsapp/campaigns/${c.id}/cancel`, {});
      void campaigns.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  return (
    <>
      {show === 'campaigns' && canReadCampaigns && (
        <Card
          title="Campaigns"
          subtitle="Send an approved template to a segment. Only customers with a recorded opt-in are messaged."
          flush
        >
          {!campaigns.data || campaigns.data.length === 0 ? (
            <EmptyState icon={<Megaphone size={22} />} title="No campaigns yet">
              Create a segment under Customers, then schedule a campaign below.
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Audience</th>
                    <th>Status</th>
                    <th>Scheduled</th>
                    <th className="table__actions">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.data.map((c) => (
                    <tr key={c.id}>
                      <td>
                        <div className="table__primary">{c.name}</div>
                        <div className="table__secondary mono">{c.templateName}</div>
                      </td>
                      <td>
                        {c.segmentName}
                        {c.totalRecipients !== null && (
                          <div className="table__secondary">{c.totalRecipients} recipients</div>
                        )}
                      </td>
                      <td>
                        <Badge tone={CAMPAIGN_TONE[c.status] ?? 'neutral'}>{c.status}</Badge>
                        {c.failureReason && (
                          <div className="table__secondary">{c.failureReason}</div>
                        )}
                      </td>
                      <td>{formatRelative(c.scheduledAt)}</td>
                      <td className="table__actions">
                        {canManageCampaigns &&
                          (c.status === 'scheduled' || c.status === 'running') && (
                            <Button size="sm" variant="secondary" onClick={() => void cancel(c)}>
                              Cancel
                            </Button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {show === 'campaigns' && canManageCampaigns && (
        <Card title="New campaign">
          {!hasApproved ? (
            <Alert tone="info">Sync templates and wait for Meta to approve one first.</Alert>
          ) : (segments.data ?? []).length === 0 ? (
            <Alert tone="info">Create a segment under Customers → Segments first.</Alert>
          ) : (
            <CampaignForm
              phones={phones}
              templates={templates}
              segments={segments.data ?? []}
              onCreated={() => void campaigns.reload()}
            />
          )}
        </Card>
      )}

      {(show === 'orderMessages' || show === 'alertMessages') && canManageChannels && (
        <section className="stack">
          <div>
            <h2 className="section-title">
              {show === 'alertMessages' ? 'Alert messages' : 'Order messages'}
            </h2>
            <p className="text-muted">
              {show === 'alertMessages'
                ? 'Sent once when a product shoppers asked about is back in stock or gets cheaper. Needs the shopper\u2019s opt-in; alerts older than 7 days are dropped.'
                : 'Sent automatically on order events. Needs the customer\u2019s opt-in; orders older than 48 hours are never messaged.'}
            </p>
          </div>
          {!hasApproved ? (
            <Alert tone="info">Sync templates and wait for Meta to approve one first.</Alert>
          ) : (
            <div className="rule-grid">
              {(rules.data?.kinds ?? [])
                .filter((kind) => ALERT_KINDS.includes(kind) === (show === 'alertMessages'))
                .map((kind) => (
                  <RuleForm
                    key={kind}
                    kind={kind}
                    rule={rules.data?.rules.find((r) => r.kind === kind)}
                    phones={phones}
                    templates={templates}
                    onSaved={() => void rules.reload()}
                  />
                ))}
            </div>
          )}
        </section>
      )}
    </>
  );
}
